import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Users, DollarSign, TrendingUp, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";


export default function AdminPanel() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is admin
        const userRes = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (userRes.ok) {
          const userData = await userRes.json();
          if (!userData.is_admin) {
            navigate('/dashboard');
            return;
          }
          setIsAdmin(true);
        } else {
          navigate('/');
          return;
        }

        // Fetch admin stats
        const statsRes = await fetch(`${API}/admin/stats`, { credentials: 'include' });
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
          setTransactions(statsData.recent_transactions || []);
        }

        // Fetch users
        const usersRes = await fetch(`${API}/admin/users`, { credentials: 'include' });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-[#6E0F1A] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6" />
              <span className="font-serif text-xl">Painel Administrativo</span>
            </div>
          </div>
          <Badge className="bg-white/20 text-white">Admin</Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-serif text-3xl text-[#6E0F1A] mb-8">Dashboard da Plataforma</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Volume Total</p>
                  <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">
                    {formatCurrency(stats?.total_volume)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#B3121B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Taxas (3%)</p>
                  <p className="text-3xl font-semibold text-[#567D50] mt-1">
                    {formatCurrency(stats?.total_platform_fees)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#567D50]/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#567D50]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Usuários</p>
                  <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">
                    {stats?.total_users || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#CFAFE6]/30 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#6E0F1A]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Sites Criados</p>
                  <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">
                    {stats?.total_sites || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#B3121B]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Transactions */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#6E0F1A]">
                Últimas Transações
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA]">
                      <TableHead className="text-[#6E0F1A]">Data</TableHead>
                      <TableHead className="text-[#6E0F1A]">Valor</TableHead>
                      <TableHead className="text-[#6E0F1A]">Taxa</TableHead>
                      <TableHead className="text-[#6E0F1A]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.slice(0, 10).map((txn, index) => (
                      <TableRow key={txn.transaction_id || index}>
                        <TableCell className="text-sm text-[#6E0F1A]/70">
                          {formatDate(txn.created_at)}
                        </TableCell>
                        <TableCell className="font-medium text-[#6E0F1A]">
                          {formatCurrency(txn.amount)}
                        </TableCell>
                        <TableCell className="text-[#567D50]">
                          {formatCurrency(txn.platform_fee)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              txn.payment_status === 'paid' 
                                ? 'bg-[#567D50]' 
                                : 'bg-yellow-500'
                            }
                          >
                            {txn.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-[#6E0F1A]/50 py-8">
                          Nenhuma transação ainda
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Users */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#6E0F1A]">
                Usuários Registrados
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA]">
                      <TableHead className="text-[#6E0F1A]">Nome</TableHead>
                      <TableHead className="text-[#6E0F1A]">E-mail</TableHead>
                      <TableHead className="text-[#6E0F1A]">Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.slice(0, 10).map((user, index) => (
                      <TableRow key={user.user_id || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {user.picture ? (
                              <img
                                src={user.picture}
                                alt={user.name}
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-[#F3C9CF] rounded-full flex items-center justify-center">
                                <span className="text-[#B3121B] font-medium">
                                  {user.name?.charAt(0) || '?'}
                                </span>
                              </div>
                            )}
                            <span className="font-medium text-[#6E0F1A]">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-[#6E0F1A]/70">
                          {user.email}
                        </TableCell>
                        <TableCell className="text-sm text-[#6E0F1A]/70">
                          {formatDate(user.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-[#6E0F1A]/50 py-8">
                          Nenhum usuário registrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="card-dashboard mt-8">
          <CardContent className="py-8 text-center">
            <p className="text-[#6E0F1A]/60 text-sm">
              <strong>Taxa da Plataforma:</strong> 3% sobre cada transação • 
              <strong> Repasse automático:</strong> 97% para os beneficiários
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
